import { Helmet } from 'react-helmet-async';
import { getShareBaseUrl, getOfficialOgImage, getDomainName } from '@/lib/seoUtils';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Empêche l'indexation (pages privées / utilitaires) */
  noindex?: boolean;
}

export const SEOHead = ({ 
  title = "MiamSécur — Rappels alimentaires grossesse et bébé",
  description = "Alertes officielles de rappels alimentaires claires et à jour, pour manger en confiance pendant la grossesse et avec bébé",
  image = getOfficialOgImage(),
  url = getShareBaseUrl() + "/",
  type = "website",
  jsonLd,
  noindex = false
}: SEOHeadProps) => {

  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "MiamSécur",
    "url": getShareBaseUrl(),
    "description": description,
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Web",
    "inLanguage": "fr-FR",
    "areaServed": [
      { "@type": "Country", "name": "France" },
      { "@type": "Country", "name": "Belgium" },
      { "@type": "Country", "name": "Switzerland" },
      { "@type": "Country", "name": "Luxembourg" },
      { "@type": "Country", "name": "Canada" }
    ],

    "audience": {
      "@type": "Audience",
      "geographicArea": {
        "@type": "Country",
        "name": "France"
      }
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "author": {
      "@type": "Organization",
      "name": "MiamSécur"
    }
  };

  const structuredData = Array.isArray(jsonLd)
    ? { "@context": "https://schema.org", "@graph": jsonLd }
    : jsonLd || defaultJsonLd;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="MiamSécur" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:domain" content={getDomainName()} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Canonical */}
      <link rel="canonical" href={url} />
      
      {/* Hreflang - variantes francophones (FR + BE/CH/LU/CA) + fallback, sur l'URL courante */}
      <link rel="alternate" hrefLang="fr-FR" href={url} />
      <link rel="alternate" hrefLang="fr-BE" href={url} />
      <link rel="alternate" hrefLang="fr-CH" href={url} />
      <link rel="alternate" hrefLang="fr-LU" href={url} />
      <link rel="alternate" hrefLang="fr-CA" href={url} />
      <link rel="alternate" hrefLang="fr" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />



      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};
